import { computed, watch } from 'vue';
import {
  TERMINOLOGY_PRESETS,
  DEFAULT_TERMINOLOGY_PRESET,
  type TerminologyPresetName,
} from '../terminology/presets';
import { i18n } from '../i18n';

/**
 * Système de terminologie 2 niveaux :
 *   1. Preset (Cas C) — un parmi 4 (sds_default, france_tpe, btp_corporate, artisans)
 *   2. Overrides ponctuels (Cas A) — { 'roles.compagnon': 'Ouvrier', ... }
 *
 * Application : on étend i18n.global.mergeLocaleMessage() pour la locale courante.
 * Les clés non-overridées tombent en fallback sur les messages de locale standard.
 *
 * NB : appel idempotent — on stocke l'état appliqué pour pouvoir le nettoyer/réappliquer.
 */

interface AppliedTerminology {
  preset: TerminologyPresetName;
  overrides: Record<string, string>;
}

let lastApplied: AppliedTerminology | null = null;

/**
 * Désaplatit { 'a.b.c': 'x' } en { a: { b: { c: 'x' } } } pour mergeLocaleMessage.
 */
function unflatten(flat: Record<string, string>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const fullKey of Object.keys(flat)) {
    const parts = fullKey.split('.');
    let cursor = out;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!cursor[parts[i]]) cursor[parts[i]] = {};
      cursor = cursor[parts[i]];
    }
    cursor[parts[parts.length - 1]] = flat[fullKey];
  }
  return out;
}

export function useTerminology() {
  /**
   * Applique un preset + overrides à toutes les locales chargées.
   * À appeler après auth.me (avec les valeurs tenant) ou après setLocale.
   */
  function applyTerminology(
    presetName: TerminologyPresetName = DEFAULT_TERMINOLOGY_PRESET,
    overrides: Record<string, string> | null = null,
  ) {
    const preset = TERMINOLOGY_PRESETS[presetName] || TERMINOLOGY_PRESETS[DEFAULT_TERMINOLOGY_PRESET];

    // Merge preset puis overrides (overrides écrasent le preset)
    const merged: Record<string, string> = { ...preset.overrides, ...(overrides || {}) };

    // Stocker pour pouvoir nettoyer plus tard
    lastApplied = { preset: presetName, overrides: merged };

    // Désaplatir et appliquer sur la locale courante (autres locales chargées au besoin)
    const tree = unflatten(merged);
    const currentLocale = i18n.global.locale.value;
    i18n.global.mergeLocaleMessage(currentLocale, tree as any);
  }

  function clearTerminology() {
    // Pour vraiment "nettoyer", il faudrait reloader la locale source.
    // En pratique, on réapplique le preset sds_default (= aucun override).
    applyTerminology(DEFAULT_TERMINOLOGY_PRESET, null);
    lastApplied = null;
  }

  // Re-applique automatiquement si la locale change (les overrides sont par-locale)
  watch(
    () => i18n.global.locale.value,
    () => {
      if (lastApplied) {
        applyTerminology(lastApplied.preset, lastApplied.overrides);
      }
    },
  );

  return {
    availablePresets: Object.values(TERMINOLOGY_PRESETS),
    applyTerminology,
    clearTerminology,
    currentPreset: computed(() => lastApplied?.preset ?? DEFAULT_TERMINOLOGY_PRESET),
  };
}
